import React, { useEffect, useState } from "react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { RELATION_LABELS } from "@/config/resources";
import { formatDate, formatDateTime, formatCurrency, laneLabel, prettify } from "@/lib/format";
import { StatusBadge } from "@/components/StatusBadge";
import NotesPanel from "@/components/NotesPanel";
import DocumentsPanel from "@/components/DocumentsPanel";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import InvitePortalDialog from "@/components/InvitePortalDialog";
import { Pencil, Trash2, ArrowRightCircle, UserPlus } from "lucide-react";

const relLabel = (resource, item) => {
  const key = RELATION_LABELS[resource] || "name";
  if (resource === "cases") return `${item.case_number ? item.case_number + " · " : ""}${item[key] || "Case"}`;
  return item[key] || item.name || item.id;
};

export default function DetailDrawer({ open, onOpenChange, config, item, onEdit, onDelete, onConvert }) {
  const { user } = useAuth();
  const [maps, setMaps] = useState({});
  const [inviteOpen, setInviteOpen] = useState(false);
  const canWrite = Array.isArray(user?.writable_resources)
    ? user.writable_resources.includes(config.key)
    : false;
  const canInvite = config.portalInvite && !user?.is_portal;

  useEffect(() => {
    if (!open || !item) return;
    const resources = [...new Set(config.fields.filter((f) => f.type === "relation").map((f) => f.resource))];
    resources.forEach(async (res) => {
      try {
        const ep = res === "users" ? "/users" : `/${res}`;
        const { data } = await api.get(ep, { params: { limit: 300 } });
        const m = {};
        (data.items || []).forEach((it) => { m[it.id] = relLabel(res, it); });
        setMaps((prev) => ({ ...prev, [res]: m }));
      } catch { /* ignore */ }
    });
    // eslint-disable-next-line
  }, [open, item?.id]);

  if (!item) return null;

  const renderValue = (f) => {
    const v = item[f.key];
    if (v === undefined || v === null || v === "") return <span className="text-muted-foreground">—</span>;
    if (f.type === "badge") return <StatusBadge value={v} />;
    if (f.key === "service_lane" || f.type === "lane") return laneLabel(v);
    if (f.type === "currency" || f.key.includes("price") || f.key.includes("cost") || f.key === "budget" || f.key === "amount" || f.key === "estimated_value") return formatCurrency(v);
    if (f.type === "date") return formatDate(v);
    if (f.type === "datetime-local") return formatDateTime(v);
    if (f.type === "relation") return (maps[f.resource]?.[v]) || <span className="text-muted-foreground">{prettify(v).slice(0, 8)}…</span>;
    if (f.type === "multiselect" && Array.isArray(v)) return v.map(prettify).join(", ");
    if (f.type === "select") return prettify(v);
    return String(v);
  };

  const title = item[config.columns.find((c) => c.primary)?.key] || item.name || item.title || config.singular;

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl p-0 overflow-y-auto mmm-scroll" data-testid="detail-drawer">
        <SheetHeader className="px-5 py-4 border-b border-[#E6EDF2] sticky top-0 bg-white z-10">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0">
              <div className="text-[11px] uppercase tracking-wide text-muted-foreground">{config.singular}</div>
              <SheetTitle className="font-display text-xl truncate text-[#252221]">{title}</SheetTitle>
            </div>
            <div className="flex gap-1.5 shrink-0">
              {canInvite && (
                <Button size="sm" variant="outline" onClick={() => setInviteOpen(true)} data-testid="invite-portal-button">
                  <UserPlus size={15} className="mr-1.5 text-[#1C4358]" /> Portal
                </Button>
              )}
              {config.convert && canWrite && item.status !== "converted" && (
                <Button size="sm" variant="outline" onClick={() => onConvert(item)} data-testid="convert-lead-button">
                  <ArrowRightCircle size={15} className="mr-1.5 text-[#9C1E22]" /> Convert
                </Button>
              )}
              {canWrite && (
                <Button size="sm" variant="outline" onClick={() => onEdit(item)} data-testid="edit-item-button">
                  <Pencil size={15} />
                </Button>
              )}
              {canWrite && (
                <Button size="sm" variant="outline" onClick={() => onDelete(item)} data-testid="delete-item-button"
                  className="text-[#9C1E22] border-[rgba(156,30,34,0.3)]">
                  <Trash2 size={15} />
                </Button>
              )}
            </div>
          </div>
        </SheetHeader>

        <Tabs defaultValue="overview" className="px-5 py-4">
          <TabsList className="grid w-full grid-cols-3 mb-4">
            <TabsTrigger value="overview" data-testid="tab-overview">Overview</TabsTrigger>
            <TabsTrigger value="notes" data-testid="tab-notes">Notes & AI</TabsTrigger>
            <TabsTrigger value="documents" data-testid="tab-documents">Documents</TabsTrigger>
          </TabsList>

          <TabsContent value="overview">
            <dl className="divide-y divide-[#F0F4F7]">
              {config.fields.filter((f) => !(f.superOnly && !user?.is_admin)).map((f) => (
                <div key={f.key} className="grid grid-cols-3 gap-3 py-2.5">
                  <dt className="text-[13px] text-muted-foreground col-span-1">{f.label}</dt>
                  <dd className="text-sm text-[#252221] col-span-2 break-words">{renderValue(f)}</dd>
                </div>
              ))}
            </dl>
          </TabsContent>

          <TabsContent value="notes">
            <NotesPanel relatedType={config.key} relatedId={item.id} branchId={item.branch_id} />
          </TabsContent>

          <TabsContent value="documents">
            <DocumentsPanel relatedType={config.key} relatedId={item.id} branchId={item.branch_id} />
          </TabsContent>
        </Tabs>

        {canInvite && (
          <InvitePortalDialog open={inviteOpen} onOpenChange={setInviteOpen} type={config.portalInvite} item={item} />
        )}
      </SheetContent>
    </Sheet>
  );
}
